"use client";

import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { KOLMOGOROV_SMIRNOV_TEST_CALCULATOR_STRINGS } from "@/components/tools/kolmogorov-smirnov-test-calculator";
import {
  ApaReport,
  Decision,
  ResultTable,
  Working,
} from "@/components/tools/stats-tests/kit";
import { EcdfPlot } from "@/components/tools/stats-tests-more/ecdf-plot";
import { t } from "@/lib/i18n/interpolate";
import {
  apaP,
  fixed,
  formatP,
  formatStat,
  type NumberFormatter,
  richTemplate,
} from "@/lib/tool-kit/stats-tests/format";
import {
  type KsOneSampleResult,
  type KsTwoSampleResult,
  lillieforsP,
  type Reference,
  referenceCdf,
} from "@/lib/tool-kit/stats-tests-more/kolmogorov-smirnov";

/** Results for the Kolmogorov–Smirnov calculator. */

type Strings = typeof KOLMOGOROV_SMIRNOV_TEST_CALCULATOR_STRINGS;

const TABLE_LIMIT = 40;

function referenceName(reference: Reference, strings: Strings): string {
  if (reference.kind === "normal") {
    return strings.normal;
  }
  return reference.kind === "uniform" ? strings.uniform : strings.exponential;
}

function referenceParams(
  reference: Reference,
  n: (v: number) => string
): string {
  if (reference.kind === "normal") {
    return `${n(reference.mean)}, ${n(reference.sd)}`;
  }
  if (reference.kind === "uniform") {
    return `${n(reference.min)}, ${n(reference.max)}`;
  }
  return n(reference.rate);
}

export function KsOneSampleResults({
  r,
  reference,
  estimated,
  alpha,
  strings,
  fmt,
}: {
  r: KsOneSampleResult;
  reference: Reference;
  estimated: boolean;
  alpha: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const n = (v: number) => formatStat(v, fmt);
  const useLilliefors = estimated && reference.kind === "normal";
  const dTwoSided = Math.max(r.dPlus, r.dMinus);
  const p = useLilliefors ? lillieforsP(dTwoSided, r.n) : r.p;
  const reject = p < alpha;
  const name = referenceName(reference, strings);
  const apa = richTemplate(strings.apaOneTemplate, {
    n: fmt(r.n),
    d: fixed(r.d, 2, fmt, false),
    p: apaP(p, fmt),
  });
  const rows = r.sorted.slice(0, TABLE_LIMIT).map((x, index) => {
    const f = referenceCdf(reference, x);
    return {
      key: `row-${index + 1}`,
      cells: [
        fmt(index + 1),
        n(x),
        n((index + 1) / r.n),
        n(f),
        n((index + 1) / r.n - f),
        n(f - index / r.n),
      ],
    };
  });
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.dStatistic} value={n(r.d)} />
        <StatCard
          emphasis
          label={useLilliefors ? strings.pValueLilliefors : strings.pValue}
          value={formatP(p, fmt)}
        />
        <StatCard label={strings.sampleSize} value={fmt(r.n)} />
        <StatCard label={strings.dPlus} value={n(r.dPlus)} />
        <StatCard label={strings.dMinus} value={n(r.dMinus)} />
      </StatGrid>
      <Decision reject={reject}>
        {t(reject ? strings.decisionReject : strings.decisionKeep, {
          alpha: fmt(alpha),
          p: formatP(p, fmt),
        })}
      </Decision>
      {estimated ? (
        <p className="rounded-lg border border-border bg-muted/40 p-4 text-foreground text-sm">
          {useLilliefors ? strings.estimatedLilliefors : strings.estimatedOther}
        </p>
      ) : (
        <p className={HINT}>
          {r.method === "exact"
            ? strings.methodExact
            : strings.methodAsymptotic}
        </p>
      )}
      {r.hasTies ? <p className={HINT}>{strings.tiesNote}</p> : null}
      <h3 className={WIDGET_H3}>{strings.gapHeading}</h3>
      <p className="text-foreground text-sm">
        {t(strings.gapOne, {
          x: n(r.atX),
          ecdf: n(r.ecdfAtX),
          dist: name,
          cdf: n(r.cdfAtX),
        })}
      </p>
      <EcdfPlot
        curve={(x) => referenceCdf(reference, x)}
        curveName={strings.plotReference}
        downloadLabel={strings.downloadPlot}
        fileName="ecdf-plot.svg"
        fmt={fmt}
        gap={{
          x: r.atX,
          low: Math.min(r.cdfAtX, r.ecdfAtX),
          high: Math.max(r.cdfAtX, r.ecdfAtX),
          label: t(strings.plotGap, { d: n(Math.max(r.dPlus, r.dMinus)) }),
        }}
        label={strings.plotLabel}
        series={[{ name: strings.plotEcdf, values: r.sorted }]}
        xTitle={strings.plotX}
        yTitle={strings.plotY}
      />
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <p className={HINT}>
        {r.n > TABLE_LIMIT ? strings.tableHidden : strings.tableHint}
      </p>
      <ResultTable
        head={[
          strings.rank,
          strings.value,
          strings.ecdf,
          strings.cdf,
          strings.upGap,
          strings.downGap,
        ]}
        rows={rows}
      />
      <Working heading={strings.howCalculated}>
        <p>
          {t(strings.workingParams, {
            dist: name,
            params: referenceParams(reference, n),
          })}
        </p>
        <p>
          {t(strings.workingD, {
            dp: n(r.dPlus),
            dm: n(r.dMinus),
            d: n(dTwoSided),
          })}
        </p>
        {r.method === "asymptotic" ? (
          <p>{t(strings.workingLambda, { lambda: n(Math.sqrt(r.n) * r.d) })}</p>
        ) : null}
      </Working>
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaHeading}
        note={strings.apaNote}
        parts={apa}
      />
    </section>
  );
}

export function KsTwoSampleResults({
  r,
  first,
  second,
  alpha,
  strings,
  fmt,
}: {
  r: KsTwoSampleResult;
  first: readonly number[];
  second: readonly number[];
  alpha: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const n = (v: number) => formatStat(v, fmt);
  const reject = r.p < alpha;
  const apa = richTemplate(strings.apaTwoTemplate, {
    d: fixed(r.d, 2, fmt, false),
    n1: fmt(r.m),
    n2: fmt(r.n),
    p: apaP(r.p, fmt),
  });
  const sortedFirst = [...first].sort((a, b) => a - b);
  const sortedSecond = [...second].sort((a, b) => a - b);
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.dStatistic} value={n(r.d)} />
        <StatCard emphasis label={strings.pValue} value={formatP(r.p, fmt)} />
        <StatCard
          label={strings.sampleSizes}
          value={`${fmt(r.m)}, ${fmt(r.n)}`}
        />
        <StatCard label={strings.dPlus} value={n(r.dPlus)} />
        <StatCard label={strings.dMinus} value={n(r.dMinus)} />
      </StatGrid>
      <Decision reject={reject}>
        {t(reject ? strings.decisionReject : strings.decisionKeep, {
          alpha: fmt(alpha),
          p: formatP(r.p, fmt),
        })}
      </Decision>
      <p className={HINT}>
        {r.method === "exact" ? strings.methodExact : strings.methodAsymptotic}
      </p>
      {r.hasTies ? <p className={HINT}>{strings.tiesNote}</p> : null}
      <h3 className={WIDGET_H3}>{strings.gapHeading}</h3>
      <p className="text-foreground text-sm">
        {t(strings.gapTwo, {
          x: n(r.atX),
          a: n(r.ecdfFirst),
          b: n(r.ecdfSecond),
        })}
      </p>
      <EcdfPlot
        curve={null}
        curveName=""
        downloadLabel={strings.downloadPlot}
        fileName="ecdf-plot.svg"
        fmt={fmt}
        gap={{
          x: r.atX,
          low: Math.min(r.ecdfFirst, r.ecdfSecond),
          high: Math.max(r.ecdfFirst, r.ecdfSecond),
          label: t(strings.plotGap, { d: n(Math.max(r.dPlus, r.dMinus)) }),
        }}
        label={strings.plotLabel}
        series={[
          { name: strings.plotSample1, values: sortedFirst },
          { name: strings.plotSample2, values: sortedSecond },
        ]}
        xTitle={strings.plotX}
        yTitle={strings.plotY}
      />
      <Working heading={strings.howCalculated}>
        <p>
          {t(strings.workingDTwo, {
            dp: n(r.dPlus),
            dm: n(r.dMinus),
            d: n(Math.max(r.dPlus, r.dMinus)),
          })}
        </p>
      </Working>
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaHeading}
        note={strings.apaNote}
        parts={apa}
      />
    </section>
  );
}
